import React from 'react';
import { Search, PenTool, Rocket, RotateCcw, Check, ArrowRight } from 'lucide-react';
import { processPhases } from '../data/mock';

const phaseIcons = [Search, PenTool, Rocket, RotateCcw];
const phaseColors = ['cyan', 'violet', 'emerald', 'amber'];

const Process = () => {
  return (
    <section id="process" className="py-16 sm:py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-violet-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
            How We Work
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
            Our Process
          </h2>
          <p className="text-slate-400 text-base sm:text-lg max-w-3xl mx-auto">
            A structured approach that creates lasting change—not a one-time event, 
            but a pathway to sustainable growth and performance.
          </p>
        </div>

        {/* Process Timeline */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 via-violet-500 via-emerald-500 to-amber-500" />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
            {processPhases.map((phase, index) => {
              const IconComponent = phaseIcons[index];
              const color = phaseColors[index];
              
              return (
                <div key={phase.phase} className="relative">
                  {/* Phase Card */}
                  <div className="bg-slate-950 rounded-2xl p-6 border border-slate-800 hover:border-slate-700 transition-all duration-300 h-full">
                    {/* Phase Icon */}
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 relative z-10 ${
                      color === 'cyan' ? 'bg-cyan-500' :
                      color === 'violet' ? 'bg-violet-500' :
                      color === 'emerald' ? 'bg-emerald-500' :
                      'bg-amber-500'
                    }`}>
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>

                    {/* Phase Number & Title */}
                    <div className="mb-3">
                      <span className={`text-xs font-bold uppercase tracking-wider ${
                        color === 'cyan' ? 'text-cyan-400' :
                        color === 'violet' ? 'text-violet-400' :
                        color === 'emerald' ? 'text-emerald-400' :
                        'text-amber-400'
                      }`}>
                        Phase {phase.phase}
                      </span>
                      <h3 className="text-xl font-bold text-white mt-1">
                        {phase.title}
                      </h3>
                    </div>

                    {/* Description */}
                    <p className="text-slate-400 text-sm mb-5 leading-relaxed">
                      {phase.description}
                    </p>

                    {/* Outcomes */}
                    <div className="space-y-2">
                      {phase.outcomes.map((outcome) => (
                        <div key={outcome} className="flex items-center gap-2">
                          <Check className={`h-4 w-4 flex-shrink-0 ${
                            color === 'cyan' ? 'text-cyan-400' :
                            color === 'violet' ? 'text-violet-400' :
                            color === 'emerald' ? 'text-emerald-400' :
                            'text-amber-400'
                          }`} />
                          <span className="text-slate-300 text-xs">{outcome}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Arrow - Mobile/Tablet */}
                  {index < processPhases.length - 1 && (
                    <div className="lg:hidden flex justify-center py-4">
                      <ArrowRight className="h-6 w-6 text-slate-600 rotate-90 sm:rotate-0" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
